import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { WorkflowService } from '@services/workflow.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MultilanguageService } from '@services/support/multilanguage.service';
import { Subscription } from 'rxjs';
import * as bowser from 'bowser';
import languagesJson from '../../../../assets/languages.json';

@Component({
    selector: 'app-token-workflow',
    templateUrl: './token-workflow.component.html',
    styleUrls: ['./token-workflow.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TokenWorkflowComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  workflowId: string;
  idToken: string;
  stepId: string;
  workflow: any = null;
  remoteUserId: string = null;
  workflowLoaded = false;

  // Form controls for download app section
  publicLink = new UntypedFormControl('');
  copyPermission = new UntypedFormControl('');

  // Workflow info
  workflowName: string;
  companyName: string;

  boxHeight = 400;
  boxWidth = 605;
  browser;
  isMobile: boolean = false;
  wfName: string;

  // Language support
  currentLang;
  allLangs;
  subscriptions: Subscription[] = [];

  // Download app
  showDownloadApp = false;
  downloadAppButtonUrl = "";

  workflowMenuClicked;
  isStart;
  contentListClicked;

  stepFilesClicked = false;
  isStepFileAvailable = false;

  constructor(
    private route: ActivatedRoute,
    private workflowService: WorkflowService,
    private http: HttpClient,
    private multilanguageService: MultilanguageService
  ) {
    this.browser = bowser.getParser(window.navigator.userAgent);
    if(this.browser.is('mobile')) {
      this.isMobile = true;
      this.boxHeight = null;
      this.boxWidth = null;
    }
  }

  async ngOnInit() {
    // Initialize language support
    this.currentLang = this.multilanguageService.currentLang;
    this.allLangs = this.multilanguageService.allLangs;
    this.subscriptions.push(this.multilanguageService.onLangChange.subscribe(change => {
      this.currentLang = change.lang;
    }));

    // Check for download app display
    let fullUrl;
    this.route.url.subscribe(url => {
      fullUrl = url.join('/');
    });
    const isTouchDevice = 'ontouchstart' in window;
    if ((this.browser.is('mobile') || this.browser.is('tablet') || (this.browser.is("macOS") && isTouchDevice)) && (fullUrl !== 'videoflow')) {
      this.showDownloadApp = true;
    }

    // Extract query parameters
    this.route.queryParams.subscribe(async params => {
      this.workflowId = params['workflowId'];
      this.idToken = params['idToken'];
      this.stepId = params['stepId'];

      // Set download app button URL
      if (this.browser.is('iOS') || (this.browser.is("macOS") && isTouchDevice)) {
        this.downloadAppButtonUrl = environment.design.appStoreLink;
      } else {
        this.downloadAppButtonUrl = environment.design.googlePlayLink + this.workflowId;
      }

      // Set public link
      const isLinkAvailable = environment.design.guestLoginLinkAvailable;
      const base = `${environment.endPoints.workflowBase}/workflow?workflowId=${this.workflowId}&idToken=${this.idToken}`;
      isLinkAvailable ? this.publicLink.patchValue(base) : this.publicLink.patchValue(window.location.href);

      // Validate required parameters
      if (!this.workflowId || !this.idToken) {
        this.error = 'Missing required parameters: workflowId and idToken are required';
        this.loading = false;
        return;
      }

      await this.authenticateAndLoadWorkflow();
    });
  }

  async authenticateAndLoadWorkflow() {
    try {
      this.loading = true;
      this.error = '';

      // Verify token using Firebase lookup API
      const firebaseApiKey = environment.firebase.apiKey;
      const lookupUrl = `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${firebaseApiKey}`;

      const lookupResult = await this.http.post<any>(lookupUrl, { idToken: this.idToken }).toPromise();

      if (!lookupResult || !lookupResult.users || lookupResult.users.length === 0) {
        this.error = 'Invalid or expired token';
        this.loading = false;
        return;
      }

      // Get user account ID from Firebase lookup response
      const userData = lookupResult.users[0];
      this.remoteUserId = userData.localId;
      console.log('Token workflow - Remote User ID:', this.remoteUserId);

      // Get workflow data using the validated idToken
      const result = await this.workflowService.getWorkflowWithToken(this.workflowId, this.idToken);

      console.log('Token workflow - API result:', result);
      console.log('Token workflow - Workflow data:', result?.data);
      console.log('Token workflow - StepId:', this.stepId);

      if (!result || !result.data) {
        this.error = 'Workflow not found';
        this.loading = false;
        return;
      }

      // Set workflow data
      this.workflow = result.data;

      // Set workflow name and company name
      this.setWorkflowName();
      this.workflowName = this.wfName; // Use the same name for both
      this.companyName = this.workflow.companyName || '';

      console.log('Token workflow - Workflow set:', this.workflow);

      // Mark as loaded and stop loading
      this.loading = false;
      this.workflowLoaded = true;

    } catch (error) {
      console.error('Error loading workflow with token:', error);
      if (error?.error?.error?.message === 'INVALID_ID_TOKEN') {
        this.error = 'Invalid or expired token';
      } else {
        this.error = error?.error?.message || 'Failed to load workflow. Please check your token and try again.';
      }
      this.loading = false;
    }
  }

  retry() {
    this.authenticateAndLoadWorkflow();
  }
  
  setWorkflowName() {
    if (!this.workflow) return;
    
    let workflowLangs = [];
    let userDefaultLang = localStorage.getItem('userDefaultLanguage');
    
    if (this.workflow.translations) {
      Object.keys(this.workflow.translations).forEach(res => {
        let workflowLang = languagesJson.languages.find(lang => lang.code == res);
        if (workflowLang) {
          workflowLangs.push(workflowLang);
        }
      });
      
      let isLang = workflowLangs.find(lang => lang.code == userDefaultLang);
      if (isLang) {
        this.wfName = this.workflow.translations[isLang.code][this.workflow.name];
      } else {
        this.wfName = this.workflow.translations[this.workflow.defaultLanguage][this.workflow.name];
      }
    } else {
      this.wfName = this.workflow.name;
    }
  }
  
  workflowSubmitted(event) {
    // Handle workflow submission if needed
  }
  
  switchWorkflowMenu() {
    this.workflowMenuClicked = !this.workflowMenuClicked;
  }
  
  workflowMenuClickedChange(event) {
    this.workflowMenuClicked = event;
  }
  
  isWorkflowStart(event) {
    this.isStart = event;
  }
  
  langChanged(lang) {
    this.wfName = lang;
  }
  
  stepFileAvailability(event) {
    this.isStepFileAvailable = event;
  }
  
  showStepFiles() {
    this.stepFilesClicked = true;
  }

  onLanguageSelected(lang: any) {
    this.multilanguageService.setCurrentLanguage(lang.code);
  }

  clickStoreLink(link) {
    if (this.copyPermission.value) {
      link.select();
      document.execCommand('copy');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
